import PatientChartHeader from 'components/organisms/patient-chart-header';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ModuleType, ReadingStatus } from 'infrastructure/enums';
import {
  useActivityFilter,
  useLinkReadingsToCall,
  useRpmPatientDetails,
} from 'infrastructure/hooks/rpm';
import { useAppDispatch, useAppSelector } from 'store';
import useUser from 'utils/useUser';
import PatientAverages from 'components/organisms/patient-averages';
import PastMedicalHistory from 'components/organisms/past-medical-history';
import CombinedClinicalActivityAndInterventions from 'components/organisms/combined-clinical-activity-and-interventions';
import PatientMedication from 'components/organisms/patient-medication';
import Services from 'components/organisms/rpm-ccm-servises';
import AdminPanelPatientDetailTabs from 'components/organisms/admin-panel/patient-detail/tabbed-forms';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import CombinedGeneralComments from 'components/molecules/general-comments/combined';
import { Routes } from 'router/routes';
import { useWebsocketRoom } from 'infrastructure/hooks/websockets/use-room';
import Trigger from 'components/organisms/admin-panel-key-notes/trigger';
import { KeyNotesDrawer } from 'components/organisms/oneplaform/key-notes-drawer';
import { ASSIGN_TOOL_ID } from 'infrastructure/consts';
import WeatherWidget from 'components/organisms/weather-widget';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { isValidGuid } from 'infrastructure/functions';
import { useReadingsCount } from 'infrastructure/hooks/rpm/activity/use-get-readings-count';
import PatientSnapshot from 'components/organisms/patient-snapshot';
import { useGetPatientAssignData } from 'infrastructure/hooks/rpm/assign-tool/use-get-patient-assing';
import {
  addAssigneePatientConfirmShowed,
  isAssigneePatientConfirmShowed,
} from 'infrastructure/functions/assigneePatientConfirmShowed';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { useAssignPatient } from 'infrastructure/hooks/rpm/assign-tool/use-set-assign-patient';
import { RunBook } from 'components/organisms/runbooks';
import { TasksDrawer } from 'components/organisms/tasks-drawer';
import CcmRpmPatientBanner from 'components/molecules/patientTopBanner/ccm-rpm-banner';
import { fullNamePresenter } from 'utils';
import CallTimer from 'components/molecules/call-timer';
import { useCallEndedEventListener } from 'infrastructure/customEvents/callEvents';
import useMonthlyInteractionAlert from 'infrastructure/hooks/utils/use-monthly-interaction-alert';
import { useUpdateInteractionStatus } from 'infrastructure/hooks/control-panel';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import PatientChartNavSection from 'components/organisms/oneplatform/patient-chart-nav-section';
import PatientChartActivity from 'components/organisms/oneplatform/patient-chart-activity';
import {
  APP_HEADER_HEIGHT,
  PAGE_HEADER_HEIGHT,
} from 'components/organisms/oneplatform/patient-chart-nav-section/constants';

import s from './styles.module.scss';

import type { IPatientInfo } from 'components/organisms/oneplaform/key-notes-drawer/types';
import type { FC } from 'react';
import type { IRpmActivityCountResponse } from 'infrastructure/interfaces/i-rpm-patient-activity';

const calculateUnreviewedReadings = (
  activityCount: IRpmActivityCountResponse | undefined,
) => {
  if (!activityCount) return -1; // as default. Need for not show the alert if activityCount is undefined

  return activityCount.critical + activityCount.normal + activityCount.risk;
};

interface UnifiedPatientProfileProps {
  sessionId?: string | null;
}

const UnifiedPatientProfile: FC<UnifiedPatientProfileProps> = ({
  sessionId,
}) => {
  const { t } = useTranslation();
  const { patientGuid } = useParams();
  const navSectionRef = useRef<HTMLDivElement>(null);
  const isAutoScrollingRef = useRef<boolean>(false);
  const hasNavInitializedRef = useRef<boolean>(false);
  const [showNavInHeader, setShowNavInHeader] = useState(false);
  const [activeNavLink, setActiveNavLink] = useState<string | null>(null);

  if (!patientGuid || !isValidGuid(patientGuid)) {
    return null;
  }

  const {
    isMobileDevice,
    isAdmin,
    isAgencyDoctor,
    isPatient,
    isMedicalAssistantRole,
    loginGuid,
  } = useUser();
  const { filter } = useActivityFilter();
  const [startTime, endTime] = useAppSelector<[number, number]>(
    (state) => state.patient.dates,
  );

  const { patientAssignData } = useGetPatientAssignData(patientGuid);
  const { assignPatient } = useAssignPatient();
  const { activityCount, refetchCount } = useReadingsCount({
    ...filter,
    reviewed: false,
    patientGuid,
    page: 1,
    items: 1,
    status: ReadingStatus.Critical,
    startTime,
    endTime,
    skipMACheck: isMedicalAssistantRole,
  });
  const { Alert, showAlert } = useAlert();

  useEffect(() => {
    if (
      isMedicalAssistantRole &&
      activityCount?.critical &&
      activityCount?.critical > 0
    ) {
      notificationController.error({
        message: t('errors.patientHasUnactionedCriticalReadings'),
        closable: true,
        duration: 0,
      });
    }
  }, [isMedicalAssistantRole, activityCount?.critical]);

  useEffect(() => {
    let throttleTimeout: ReturnType<typeof setTimeout> | null = null;

    const handleScroll = () => {
      if (throttleTimeout) return;

      throttleTimeout = setTimeout(() => {
        if (navSectionRef.current) {
          const rect = navSectionRef.current.getBoundingClientRect();
          setShowNavInHeader(
            rect.bottom < APP_HEADER_HEIGHT + PAGE_HEADER_HEIGHT + 50,
          );
        }
        throttleTimeout = null;
      }, 200);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (throttleTimeout !== null) {
        clearTimeout(throttleTimeout);
      }
    };
  }, []);

  const unactionedReadingsCount = calculateUnreviewedReadings(activityCount);

  useEffect(() => {
    if (
      unactionedReadingsCount === 0 &&
      loginGuid === patientAssignData?.assignee?.loginGuid &&
      !isAssigneePatientConfirmShowed(patientGuid)
    ) {
      showAlert({
        title: t('labels.unassign'),
        message: t('messages.unassigngConfirmMessage'),
        messageTitle: t('messages.unassigngConfirmTitle'),
        type: 'primary',
        successBtnLabel: t('controls.yes'),
        rejectBtnLabel: t('controls.no'),
      }).then((answer) => {
        if (answer) {
          addAssigneePatientConfirmShowed(patientGuid);
          assignPatient({
            patientGuid,
            action: 'unassign',
          });
        }
      });
    }
  }, [
    unactionedReadingsCount,
    patientAssignData?.assignee?.loginGuid,
    loginGuid,
  ]);

  const {
    query: { patient, isLoading },
    clinicGuid,
    physicianFullName,
  } = useRpmPatientDetails({ patientGuid });

  useWebsocketRoom('rpmPatientProfile', { patientGuid });

  const { isReadOnlyRole } = useCheckRoleAccess();

  const [isKeyNoteDrawerOpen, setIsKeyNoteDrawerOpen] = useState(false);
  const [hasAssignToolNearby, setHasAssignToolNearby] = useState(false);

  useEffect(() => {
    const ROOT_ELEMENT = document.getElementById('root');
    const hasAssignTool = !!ROOT_ELEMENT?.querySelector(`#${ASSIGN_TOOL_ID}`);
    setHasAssignToolNearby(hasAssignTool);
  }, []);

  const handleOpenKeyNoteDrawer = useCallback(() => {
    setIsKeyNoteDrawerOpen(true);
  }, []);

  const handleCloseKeyNoteDrawer = useCallback(() => {
    setIsKeyNoteDrawerOpen(false);
  }, []);

  const patientFullName = patient?.profile
    ? fullNamePresenter({
        firstName: patient.profile.firstName,
        middleName: patient.profile.middleName,
        lastName: patient.profile.lastName,
        title: patient.profile.title,
      })
    : undefined;

  const patientKeyNoteInfo: IPatientInfo | null = patient
    ? {
        guid: patientGuid,
        firstName: patient.profile.firstName ?? '',
        lastName: patient.profile.lastName ?? '',
        birthDate: patient.profile.birthDate ?? '',
        language: patient.profile.language ?? '',
        doctorName: physicianFullName ?? '',
        status: patient.profile.status,
        ccmStatus: patient.profile.ccmStatus,
      }
    : null;

  const patientSla = patient?.clinic?.serviceDeliveryThresholds ?? undefined;

  const { patientInfo } = usePatientInfo({
    patientGuid,
  });
  const { readingIds } = useAppSelector((state) => state.linkedCallsReadings);
  const { resetState } = useAppDispatch().linkedCallsReadings;
  const { linkReadingsToCall } = useLinkReadingsToCall();
  const { updateInteractionStatus } = useUpdateInteractionStatus();
  const { MonthlyInteractionAlert, showMonthlyInteractionAlert } =
    useMonthlyInteractionAlert({ patientGuid });

  useCallEndedEventListener(async (event) => {
    const MIN_SUCCESSFUL_CALL_DURATION = 30;
    const details = event.detail;

    const interactionStatus = patientInfo?.interactionStatus;
    if (
      interactionStatus === false &&
      details.callDuration >= MIN_SUCCESSFUL_CALL_DURATION &&
      details.patientGuid === patientGuid
    ) {
      const result = await showMonthlyInteractionAlert({
        interactionType: 'call',
      });
      if (result) {
        await updateInteractionStatus({
          patientGuid,
          isInteracted: true,
        });
      }
    }

    if (
      details.module === ModuleType.RPM &&
      readingIds.length > 0 &&
      details.callDuration >= MIN_SUCCESSFUL_CALL_DURATION &&
      details.patientGuid === patientGuid
    ) {
      linkReadingsToCall(
        {
          callId: details.callId,
          readings: readingIds,
          patientGuid,
        },
        {
          onSettled: () => {
            resetState();
          },
        },
      );
    }
  });

  return (
    <div className={s.wrapper}>
      <CallTimer patientGuid={patientGuid} />
      {MonthlyInteractionAlert}
      <PatientChartHeader
        patientGuid={patientGuid}
        clinicGuid={clinicGuid}
        module={ModuleType.RPMCCM}
        goBackPath={Routes.dashboard}
        title={t('labels.patientProfileTitle')}
        patientName={patientFullName}
        sla={patientSla}
        isShowEmailTag={patient?.profile?.emailSubscription === false}
        activityCount={activityCount}
        showNavSection={showNavInHeader}
        activeNavLink={activeNavLink}
        onActiveNavLinkChange={setActiveNavLink}
        isAutoScrollingRef={isAutoScrollingRef}
        hasNavInitializedRef={hasNavInitializedRef}
      />
      {!isMobileDevice &&
        (isAdmin || isAgencyDoctor) &&
        createPortal(
          <Trigger
            setExpanded={() => {}}
            onOpen={handleOpenKeyNoteDrawer}
            opened
            isStandalone
            hasAssignToolNearby={hasAssignToolNearby}
          />,
          document.getElementById('root')!,
        )}
      {!isMobileDevice && clinicGuid && (isAdmin || isAgencyDoctor) && (
        <RunBook clinicGuid={clinicGuid} patientGuid={patientGuid} />
      )}
      <WeatherWidget
        city={patient?.profile?.city}
        state={patient?.profile?.state}
        timezone={patient?.profile?.timezone}
      />
      <div className={s.body}>
        {!isPatient && (
          <CcmRpmPatientBanner
            patientGuid={patientGuid}
            careAgents={patient?.careAgents ?? []}
            patientDetailsLoading={isLoading}
          />
        )}
        <div ref={navSectionRef}>
          <PatientChartNavSection
            module={ModuleType.RPMCCM}
            activityCount={activityCount}
            activeLink={activeNavLink}
            onActiveLinkChange={setActiveNavLink}
            isAutoScrollingRef={isAutoScrollingRef}
            hasInitializedRef={hasNavInitializedRef}
          />
        </div>
        <PatientSnapshot patientGuid={patientGuid} />
        <PatientChartActivity
          patientGuid={patientGuid}
          activityCountByStatus={activityCount}
          onReadingsRefetch={refetchCount}
        />
        <PatientAverages patientGuid={patientGuid} endTime={endTime} />
        <PastMedicalHistory patientGuid={patientGuid} />
        <CombinedClinicalActivityAndInterventions patientGuid={patientGuid} />
        <PatientMedication
          patientGuid={patientGuid}
          module={ModuleType.RPMCCM}
          sessionId={sessionId}
        />
        <Services patientGuid={patientGuid} />
        <div id="user-profile">
          <AdminPanelPatientDetailTabs
            patientGuid={patientGuid}
            module={ModuleType.RPMCCM}
            readOnly={isReadOnlyRole}
          />
        </div>
        <CombinedGeneralComments
          patientGuid={patientGuid}
          interactionStatus={patient?.profile.interactionStatus}
        />
        {Alert}
      </div>
      {(isAdmin || isAgencyDoctor) && (
        <TasksDrawer patientGuid={patientGuid} patientName={patientFullName} />
      )}
      <KeyNotesDrawer
        open={isKeyNoteDrawerOpen}
        onClose={handleCloseKeyNoteDrawer}
        patientGuid={patientGuid}
        patientInfo={patientKeyNoteInfo}
      />
      {Alert}
    </div>
  );
};
export default UnifiedPatientProfile;
